import { STRINGS } from '@/constants/strings';
import type { CoGuardianMutualKidVM, CoGuardianProfileVM } from '@/features/guardian-player/types';
import type { CoGuardianMutualKid, CoGuardianProfile } from '@/services/get-co-guardian';

import { DATE_FORMATS } from '@/utils/date-formats';
import { formatPlayerGender } from '@/utils/player-gender';

import { formatGuardianApiDate } from './date';

const strings = STRINGS.guardianPlayer.coGuardianProfile;

/** Relationship/role → a human label, falling back to a title-cased version of the input. */
export function coGuardianRoleLabel(relationship?: string, role?: string): string {
  const table = strings.relationships as Record<string, string>;
  const relKey = (relationship ?? '').toLowerCase();
  if (table[relKey]) return table[relKey];
  const roleKey = (role ?? '').toLowerCase();
  if (table[roleKey]) return table[roleKey];
  if (relationship) return relationship.charAt(0).toUpperCase() + relationship.slice(1);
  return strings.relationships.other;
}

/** "Age 8 · Level 3", or "Level 3" when age is unknown. */
function mutualKidMetaLabel(age: number | null, level: number | null): string {
  const safeLevel = level ?? 1;
  if (age === null || age === undefined) {
    return strings.kidLevelOnly.replace('{level}', String(safeLevel));
  }
  return strings.kidMeta.replace('{age}', String(age)).replace('{level}', String(safeLevel));
}

/** Capitalizes the first letter (API values are inconsistent about casing, e.g. dominantHand
 *  is lowercase while gender is already capitalized). */
function capitalize(value?: string): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed) return undefined;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

/** Street + city + state, comma-joined, skipping empty parts; `undefined` when all are empty. */
function mutualKidAddressLabel(address?: CoGuardianMutualKid['address']): string | undefined {
  const parts = [address?.street1, address?.street2, address?.city, address?.state]
    .map(part => part?.trim())
    .filter((part): part is string => Boolean(part));
  return parts.length > 0 ? parts.join(', ') : undefined;
}

function toMutualKidVM(kid: CoGuardianMutualKid): CoGuardianMutualKidVM {
  const age = kid.age ?? null;
  const level = kid.level ?? null;
  // The co-guardian's access date now comes from `createdOn`; fall back to the legacy
  // `since` field while older payloads may still be cached.
  const sinceMonthYear = formatGuardianApiDate(kid.createdOn ?? kid.since, 'MMM yyyy');
  const birthdate = formatGuardianApiDate(kid.dob, DATE_FORMATS.TEXT);
  const zipcode = kid.address?.zipcode?.trim();

  return {
    id: kid.id,
    name: kid.name,
    age,
    level,
    avatarUrl: kid.avatarUrl,
    metaLabel: mutualKidMetaLabel(age, level),
    sinceLabel: sinceMonthYear ? strings.kidSince.replace('{date}', sinceMonthYear) : undefined,
    birthdateLabel: birthdate || undefined,
    genderLabel: formatPlayerGender(kid.gender),
    heightLabel: kid.height ? `${kid.height}"` : undefined,
    dominantHandLabel: capitalize(kid.dominantHand),
    addressLabel: mutualKidAddressLabel(kid.address),
    zipcodeLabel: zipcode || undefined,
  };
}

/** Maps the co-guardian profile API payload into the screen's identity + mutual-kids view model. */
export function buildCoGuardianProfile(
  profile: CoGuardianProfile | undefined,
): CoGuardianProfileVM | undefined {
  if (!profile) return undefined;

  const joinedMonthYear = formatGuardianApiDate(profile.joinedSince, 'MMM yyyy');

  return {
    guardianUid: profile.guardianUid,
    name: profile.name?.trim() || strings.fallbackName,
    avatarUrl: profile.photoUrl,
    roleLabel: coGuardianRoleLabel(profile.relationship, profile.role),
    joinedLabel: joinedMonthYear
      ? strings.joinedSince.replace('{date}', joinedMonthYear)
      : undefined,
    email: profile.email,
    mutualKids: (profile.mutualKids ?? []).map(toMutualKidVM),
    role: profile.role,
  };
}
